import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Icon from '@components/icon/Icons'

import { EtatDesLieuxTypeEnum } from '@api/enum'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'

interface Props {
  group: EtatDesLieuxResponse[]
  onClick: () => void
  manifestation?: ManifestationResponse
  tenant?: Tenant
}

const EDLGroupedCard = ({ group, onClick, manifestation, tenant }: Props) => {
  const i18n = useI18n()
  const [, currentBreakpoint] = useBreakpoint()
  const isPhone = currentBreakpoint === 'phone'

  const nameEvent = !!manifestation ? manifestation.libelle : !!tenant ? tenant.tenant.nomLocatairePermanent : ''

  const renderRequestIndicator = (numberToDisplay: number) => (
    <RequestIndicator>
      <RequestIndicatorTotal>{numberToDisplay}</RequestIndicatorTotal>
    </RequestIndicator>
  )

  const renderNumberEDL = () => {
    const IN = group.filter((g) => g.type === EtatDesLieuxTypeEnum.ENTREE)
    const OUT = group.filter((g) => g.type === EtatDesLieuxTypeEnum.SORTIE)

    return (
      <SubInfos>
        {renderRequestIndicator(IN.length)}
        <EventText padding primary>
          {i18n.t(`screens.edlList.entry`)}
        </EventText>
        {renderRequestIndicator(OUT.length)}
        <EventText primary>{i18n.t(`screens.edlList.exit`)}</EventText>
      </SubInfos>
    )
  }

  return (
    <Container key={nameEvent} activeOpacity={0.9} onPress={onClick}>
      <SafeContainer>
        <TopContainer>
          <Infos>
            <Name numberOfLines={2} ellipsizeMode="tail">
              {nameEvent}
            </Name>

            {renderNumberEDL()}
          </Infos>
          {isPhone && (
            <IconContainer>
              <Icon name="arrow" size={20} color={theme.colors.primary} />
            </IconContainer>
          )}
        </TopContainer>
      </SafeContainer>
    </Container>
  )
}

export default EDLGroupedCard

const Container = styled(TouchableOpacity)`
  margin: 4px;
  border-radius: 8px;
  background-color: white;
  ${(props) => props.theme.breakpoint !== 'phone' && `width:360px;`}
  ${(props) => props.theme.constants.shadow};
`

const SafeContainer = styled(View)`
  border-radius: 8px;
  overflow: hidden;
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'row' : 'column')};
  ${(props) => props.theme.breakpoint === 'phone' && `align-items: center;`}
`

const TopContainer = styled(View)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '16px' : '24px')};
  background-color: ${(props) => props.theme.colors.background};
  flex-direction: row;
  align-items: center;
  ${(props) => props.theme.breakpoint === 'phone' && `flex: 1;`}
`

const Name = styled(Text)<{ isList?: boolean }>`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Body : props.theme.fonts.Headline)};
`

const EventText = styled(Text)<{ primary?: boolean; row?: boolean; padding?: boolean }>`
  ${(props) => !!props.padding && 'padding-right: 32px;'}
  ${(props) => !props.padding && !props.row && 'padding-right: 8px;'}
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => (props.primary ? props.theme.colors.primary : props.theme.colors.secondaryText)};
`

const RequestIndicator = styled(View)`
  height: ${(props) => (props.theme.breakpoint === 'phone' ? 24 : 32)}px;
  width: ${(props) => (props.theme.breakpoint === 'phone' ? 24 : 32)}px;
  border-radius: 36px;
  background-color: ${(props) => props.theme.colors.secondaryActive};
  align-items: center;
  justify-content: center;
  margin-right: 16px;
`

const RequestIndicatorTotal = styled(Text)`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Caption1 : props.theme.fonts.Subheadline)};
  text-align: center;
  color: ${(props) => props.theme.colors.primaryText};
  align-items: center;
`

const IconContainer = styled(View)`
  padding-right: 16px;
`

const Infos = styled(View)`
  flex: 1;
`

const SubInfos = styled(View)`
  flex-direction: row;
  padding-top: ${(props) => (props.theme.breakpoint === 'phone' ? '8' : '12')}px;
  align-items: center;
`
